import {
  DOC2API_URL,
  WEB_SOCKET_URL,
  buildServiceUrl,
  buildAuthUrl,
  clearPersistedAuthRegion,
  getCurrentApiRegion,
  getCurrentAuthRegion,
  isRegionalStage,
  onApiBaseUrlChange,
  onApiRegionChange,
  setApiRegion,
  setAuthRegion,
  type ApiRegion,
} from 'src/pages/api-base-url';

import { authApiService } from 'src/services';

export {
  DOC2API_URL,
  WEB_SOCKET_URL,
  buildAuthUrl,
  clearPersistedAuthRegion,
  getCurrentApiRegion,
  getCurrentAuthRegion,
  isRegionalStage,
  onApiBaseUrlChange,
  onApiRegionChange,
  setApiRegion,
  setAuthRegion,
  type ApiRegion,
};

// ──────────────────────────────────────────────────────────────────────────
//  Base URLs
//  • Region prefix (eu./us.) is added by buildServiceUrl() on prod, dev,
//    and sandbox. stage, demo, local → no region prefix.
//  • AUTH_URL never gets a region prefix on any stage.
//  • Local-dev port mappings and the Quasar /<branch>-<service> proxy paths
//    are absorbed by buildServiceUrl(); this block stays a flat table.
//  • WebSocket traffic always lands on a sticky-session host:
//    api-sticky for the main app socket, docnet-sticky for DocNet.
//    Operator chat WS has no sticky variant yet — it rides the regional
//    operator host until backend ships one.
//  • Region is decided once per session from /profile and baked in here
//    at module load. setApiRegion() reloads the page when it changes.
// ──────────────────────────────────────────────────────────────────────────

export const DOCFLOW_URL              = buildServiceUrl('docflow');
export const OPERATOR_URL             = buildServiceUrl('operator');
export const IDEAS_URL                = buildServiceUrl('idea');
export const AUTO_ACCOUNTING_URL      = buildServiceUrl('autoaccounting');
export const PO_MATCH_SERVICE_URL     = buildServiceUrl('pomatch');
export const IMPORT_FTP_URL           = buildServiceUrl('import-ftp');
export const IMPORT_EMAIL_URL         = buildServiceUrl('import-email');
export const DOCNET_URL               = buildServiceUrl('docnet');
export const DOCNET_STICKY_URL        = buildServiceUrl('docnet-sticky');
export const FULL_TEXT_SEARCH_API_URL = buildServiceUrl('fulltext');

export const AUTH_URL                 = buildAuthUrl();

// ─── Non-regional infrastructure ─────────────────────────────────────────

const rawBranch = process.env.APP_VUE_BRANCH || '';
// ruiz customer points at production URLs even though APP_VUE_BRANCH is set.
const branch = rawBranch === 'ruiz' ? '' : rawBranch ? rawBranch + '.' : '';

// True only when running against the customer-facing Sandbox env
// (APP_VUE_BRANCH=sandbox). Dev and Stage are not Sandbox.
export function isSandboxEnvironment(): boolean {
  return getCurrentEnvironment() === 'sandbox';
}

const TERRAFORM_URL = 'https://' + branch + 'terraform.polydocs.io';
const INSIGHT_URL = 'https://' + branch + 'insight.polydocs.io';

export const FELLOWKV2_URL =
  'https://' + branch + 'fellowkv2.cloudintegration.eu';
export const SUPPORT_URL =
  'https://' + branch + 'cloudintegration.eu/doc2_support';

const CLOUD_INTEGRATION_URL = 'https://docbits.com/';

// Discord Bot Invite URL (per-environment client IDs)
const DISCORD_BOT_CLIENT_IDS: Record<string, string> = {
  'dev.': '1487054980803461120',
  'stage.': '', // TODO: add stage bot client ID
  'sandbox.': '', // TODO: add sandbox bot client ID
  'demo.': '', // TODO: add demo bot client ID
};
const discordClientId = DISCORD_BOT_CLIENT_IDS[branch] ?? ''; // default: prod
export const DISCORD_BOT_INVITE_URL = discordClientId
  ? `https://discord.com/oauth2/authorize?client_id=${discordClientId}&permissions=277025508352&scope=bot+applications.commands+guilds+messages.read`
  : '';

// Returns 'dev', 'stage', 'prod', etc. (no trailing dot).
export function getCurrentEnvironment(): string {
  const raw = process.env.APP_VUE_BRANCH || 'dev';
  return raw.endsWith('.') ? raw.slice(0, -1) : raw;
}

const apiInternal = {
  /**
   * Audit trail / activity logs endpoint
   */
  audit_logs: DOC2API_URL + '/api/audit-logs',

  /**
   * Audit metrics endpoint for aggregated statistics
   */
  audit_metrics: DOC2API_URL + '/api/audit-logs/metrics',

  /**
   * If user set webhook as export integration, in order to set webhook/idm/ion as export integration, you need to delete previous configuration
   */
  delete_existing_export_configuration:
    DOC2API_URL + '/configurations/remove_configuration',
  /**
   * API to check whether already export configurations defined or not
   */
  export_configuration_existence:
    DOC2API_URL + '/configurations/validate_export_configuration',
  /**
   * Watchdog configuration APIs
   */
  create_or_update_watchdog_config: AUTH_URL + '/watchdog_config/config',
  get_watchdog_config: AUTH_URL + '/watchdog_config/config',
  watchdog_status: DOC2API_URL + '/watchdog/status',
  watchdog_commands: DOC2API_URL + '/watchdog/commands',
  // New v3 multi-configuration endpoints
  watchdog_configurations: DOC2API_URL + '/watchdog/configurations',
  watchdog_templates: DOC2API_URL + '/watchdog/templates',
  // XSLT validation endpoint
  xslt_validate: DOC2API_URL + '/xml_export/xslt/validate',
  xslt_doc_types: DOC2API_URL + '/xml_export/xslt/doc-types',

  validate_and_export: DOC2API_URL + '/document/validate_and_export/',

  do_alternate_export: DOC2API_URL + '/export/export_with_config',
  /**
   * Final API to export data after user successfully selected export integration
   */
  export_data: DOC2API_URL + '/export/export_data',

  get_toml_file: DOC2API_URL + '/export/get_toml_file',

  get_export_configuration_mapping_samples:
    DOC2API_URL + '/configurations/get_export_configuration_mapping_samples',

  save_export_configuration_mapping_sample:
    DOC2API_URL + '/configurations/save_export_configuration_mapping_sample',

  delete_export_configuration_mapping_sample:
    DOC2API_URL + '/configurations/delete_export_configuration_mapping_sample/',

  export_configuration_mapping:
    DOC2API_URL + '/configurations/export_configuration_mapping/',
  /**
   * User selecting WEBHOOK as his exporting integration method
   */
  define_webhook_export:
    DOC2API_URL + '/configurations/configure_webhook_export',
  /**
   * User selecting INFOR IDM/ION as his exporting integration method
   */
  define_infor_export: DOC2API_URL + '/configurations/configure_infor_export',
  define_vertex_export: DOC2API_URL + '/configurations/configure_vertex_export',
  define_generic_api_export: DOC2API_URL + '/export-configurations/generic-api',
  /**
   * get all export configurations
   */
  get_export_configurations: DOC2API_URL + '/configurations/get_configurations',
  /**
   * Download export configurations
   */
  configurations_download: DOC2API_URL + '/configurations/download/',
  /**
   * test_sftp_connection
   */
  test_sftp_connection: DOC2API_URL + '/configurations/test_sftp_connection',
  /**
   * test_smb_connection
   */
  test_smb_connection: DOC2API_URL + '/configurations/test_smb_connection',
  /**
   * test_infor_connection
   */
  test_infor_connection: DOC2API_URL + '/configurations/test_infor_connection',

  /**
   * Fetch grid data on dashboard
   */

  get_dashboard_data: DOC2API_URL + '/document/dashboards',

  /**
   * Fetch grid data on dashboard
   */

  export_dashboard_data: DOC2API_URL + '/document/exports/dashboard',

  /**
   * warehouse dashboard
   */

  get_warehouse_dashboard: DOC2API_URL + '/clickhouse/dashboard',
  /**
   * Upload invoice
   * MARKED TOBE REMOVED
   */
  process_document: FELLOWKV2_URL + '/doc2/process_document',
  /**
   * DOC2 Upload invoice
   */
  process_document_doc2: DOC2API_URL + '/document/process',
  /**
   * DOC2 Upload multipal Documents
   */
  process_documents: DOC2API_URL + '/document/process_documents',
  /**
   * DOC2 get Extracted Document By id
   */
  get_extracted_document_by_id: DOC2API_URL + '/document/extraction/result',

  /**
   * DOC2 Clear extracted cached data
   */

  open_search_documents:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/search_documents',
  open_search_facets:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/search_facets',
  open_search_suggestions:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/search_suggestions',
  open_search_similar:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/search_similar',
  delete_open_search_index:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/delete_index',
  create_open_search_index:
    FULL_TEXT_SEARCH_API_URL + '/opensearch/create_index',
  clear_extracted_data_cache:
    DOC2API_URL + '/document/extraction/cache/clear',
  // DOCBITS get document status

  get_document_status: DOC2API_URL + '/document/get_doc_status_info',
  get_extraction_explanations: DOC2API_URL + '/documents',
  fellow_kv_rules: DOC2API_URL + '/fellow_kv_rules',

  get_response_coa: DOC2API_URL + '/supplier/get_response_coa',
  reject_coa: DOC2API_URL + '/supplier/reject_coa',
  get_coa_mapping: DOC2API_URL + '/supplier/get_coa_mapping',
  add_coa_mapping: DOC2API_URL + '/supplier/add_coa_mapping',
  /**
   * DOC2 Get Document by id
   */
  get_document_by_id: DOC2API_URL + '/document',
  /**
   * Dashboard search-field catalog (single source of truth, shared with
   * backend). Frontend fetches at app startup and hydrates the local
   * FIELD_CATALOG so autocomplete + chip-editor + raw_query parser stay in
   * lockstep with the backend's routing rules. See backend file
   * doc2-api/routers/document_process/search_fields_catalog.py.
   */
  search_fields_catalog: DOC2API_URL + '/document/search/fields',
  /**
   * DOC2 Save Document
   */
  save_document: DOC2API_URL + '/document/update/',
  /**
   * DOC2 Save Document
   */
  notify_requisitioner: DOC2API_URL + '/document/notify/requisitioner',
  /**
   * DOC2 Delete Document
   */
  delete_document: DOC2API_URL + '/document/delete/',
  /**
   * DOC2 Delete Document
   */
  delete_documents: DOC2API_URL + '/document/delete_documents',
  /**
   * DOC2 assign Document
   */
  assign_document: DOC2API_URL + '/document/assign/',
  /**
   * DOC2 restart Document
   */
  restart_document: DOC2API_URL + '/document/restart/',
  /**
   * DOC2 restart Documents
   */
  restart_documents: DOC2API_URL + '/document/restart_documents',
  /**
   * DOC2 Document Annotations
   */
  get_doc_annotations: DOC2API_URL + '/document_annotation/get_annotations',
  annotate_document: DOC2API_URL + '/document_annotation/annotate_document',
  stamp_document: DOC2API_URL + '/document_annotation/stamp_document',
  edit_document_stamp: DOC2API_URL + '/document_annotation/edit_document_stamp',
  edit_annotation: DOC2API_URL + '/document_annotation/edit_annotation',
  remove_annotation: DOC2API_URL + '/document_annotation/remove_annotation',
  /**
   * DOC2 save_and_validate Document
   */
  save_and_validate: DOC2API_URL + '/document/save_and_validate/',

  /**
   * DOC2 merge Documents
   */
  merge_documents: DOC2API_URL + '/document/merge',
  /**
   * DOC2 append files to Documents
   */
  append_files_to_document: DOC2API_URL + '/document/append_files_to_document',

  /**
   * DOC2 restart export
   */
  restart_export: DOC2API_URL + '/document/restart_export',
  /**
   * DOC2 restart export Documents
   * */
  restart_export_documents: DOC2API_URL + '/document/restart_export_documents',
  /**
   * Get export execution logs for a document
   */
  export_logs: DOC2API_URL + '/export-configurations/export_logs/',
  /**
   * get table
   */
  get_table_default: DOC2API_URL + '/table_extraction/get_table',
  /**
   * DOC2 restart with new classification Document
   */
  restart_with_new_classification:
    DOC2API_URL + '/document/restart_with_new_classification/',
  /**
   * Get field value against co-ordinates
   * Call this when you draw an area against a field to fetch value of that field
   */
  get_field_value: DOC2API_URL + '/extract/get_text',
  /**
   * On drawing an area to get correct data for table
   */
  get_table_data: FELLOWKV2_URL + '/doc2/get_table',

  /**
   * get token - Using v2 API endpoint
   */
  get_token: AUTH_URL + '/v2/oauth2/token',
  /**
   * get profile - Using v2 API endpoint
   */
  get_profile: AUTH_URL + '/v2/oauth2/profile',
  /**
   * get subscriptions for app
   */
  get_subscriptions_for_app:
    AUTH_URL + '/subscription/get_subscriptions_for_app',

  /**
   * API Key Management (v2 endpoints via doc2-api proxy)
   */
  api_keys_list: DOC2API_URL + '/api/api-keys',
  api_keys_create: DOC2API_URL + '/api/api-keys',
  api_keys_get: DOC2API_URL + '/api/api-keys/', // + key_id
  api_keys_update: DOC2API_URL + '/api/api-keys/', // + key_id
  api_keys_revoke: DOC2API_URL + '/api/api-keys/', // + key_id
  create_docflow_subscription: AUTH_URL + '/subscription/docflow_subscription',
  create_subscription: AUTH_URL + '/subscription/create',
  end_subscription: AUTH_URL + '/subscription/end_subscription',
  create_subscription_usage: AUTH_URL + '/app_usage_log/create',
  get_all_subscription_plans: AUTH_URL + '/subscription_plan/list',
  create_bod_stream_subscription: AUTH_URL + '/subscription/create',
  delete_bod_stream_subscription:
    AUTH_URL + '/subscription/bod_connection_stream_subscription',
  create_bod_stream_subscription_usage: AUTH_URL + '/app_usage_log/create',
  /**
   * get supplier organisations for organisation
   */
  get_approved_supplier_organisations:
    AUTH_URL + '/supplier/get_approved_supplier_organisations',
  /**
   * register user
   */
  register_user: AUTH_URL + '/user/create_demo_user',
  /**
   * Feature subscription lifecycle endpoints
   */
  feature_subscription_create: AUTH_URL + '/feature_subscription/create',
  feature_subscription_list: AUTH_URL + '/feature_subscription/list',
  feature_subscription_base: AUTH_URL + '/feature_subscription/',
  /**
   * get users
   */
  get_users: DOC2API_URL + '/users/get_users',
  /**
   * get user details with groups and sub_organisations
   */
  get_user_details_with_group_and_sub_organisations:
    DOC2API_URL + '/users/get_user_with_groups_and_sub_organisations_sync',
  /**
   * check user online
   */
  check_user_online: DOC2API_URL + '/users/check_user_online',
  /**
   * create users
   */
  create_users: DOC2API_URL + '/users/create',
  /**
   * delete users
   */
  delete_user: DOC2API_URL + '/users/delete/',
  /**
   * update users
   */
  update_user: DOC2API_URL + '/users/update/',

  /**
   * Email Templates API
   */
  email_template_fetch: DOC2API_URL + '/email-template/get_email_templates',
  email_template_get_by_doc_type:
    DOC2API_URL + '/email-template/get_email_templates_by_doc_type',
  email_template_get: DOC2API_URL + '/email-template/get_email_template',
  email_template_update: DOC2API_URL + '/email-template/update_email_template',
  email_template_delete: DOC2API_URL + '/email-template/delete',
  email_template_create: DOC2API_URL + '/email-template/add_email_template',
  email_template_upload_file: DOC2API_URL + '/email-template/upload_files',
  email_template_get_data: DOC2API_URL + '/email-template/template_datas',
  email_template_post_test: DOC2API_URL + '/email-template/send_test_email',
  email_template_get_file:
    DOC2API_URL + '/email-template/get_email_template_assets',

  /**
   * Email Document Creation API
   * Endpoints for extracting and creating documents from emails
   */
  email_extract: DOC2API_URL + '/email/extract',
  email_document_create: DOC2API_URL + '/document/create-from-email',
  email_auto_process: DOC2API_URL + '/email/auto-process',
  email_extraction_result: DOC2API_URL + '/email/extraction-result',

  /**
   * Fetch SMAL Configurations
   */
  fetch_saml_configurations: AUTH_URL + '/saml/view_doc2_configurations',
  /**
   * Fetch SMAL METADATA info
   */
  fetch_saml_meta: AUTH_URL + '/saml/view_configurations',
  /**
   * Fetch SMAL METADATA info
   */
  save_saml_configurations: AUTH_URL + '/saml/configure',
  /**
   * Fetch SMAL METADATA info
   */
  saml_download_meta: AUTH_URL + '/saml/download_configured_metadata',

  /**
   * Delete SMAL METADATA info
   */
  delete_saml_configurations: AUTH_URL + '/saml/delete',

  // Auth Analytics (v2 endpoints)
  /**
   * Get comprehensive auth security metrics
   * Query params: time_period (today|week|30days)
   */
  get_auth_metrics: AUTH_URL + '/v2/analytics/auth/metrics',
  /**
   * Get suspicious IP address details
   * Query params: time_period (today|week|30days)
   */
  get_auth_ip_attacks: AUTH_URL + '/v2/analytics/auth/ip-attacks',

  /**
   * get  settings
   */
  get_all_documents_field_settings:
    DOC2API_URL + '/field/get_field_settings_all_document_types',
  /**
   * copy fields setting from doc_type
   */
  copy_fields_setting_from_doc_type:
    DOC2API_URL + '/field/copy_fields_setting_from_doc_type',
  /**
   * update field settings
   */
  update_field_settings: DOC2API_URL + '/field/update_field_settings',
  /**
   * settings restore defaults
   */
  settings_restore_defaults: DOC2API_URL + '/field/settings_restore_defaults',
  /**
   * get settings by doc type
   */
  get_field_settings_by_type: DOC2API_URL + '/field/get_field_settings',
  // restore supplier fields
  restore_supplier_fields:
    DOC2API_URL + '/field/settings_restore_defaults_supplier_layout',
  /**
   * get all doc types
   */
  get_doc_types: DOC2API_URL + '/configurations/document_types',
  /**
   * get purchase order data
   */
  get_purchase_order_data:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_purchase_order_lines',
  get_receive_delivery:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_receive_delivery',
  get_receive_delivery_lines:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_receive_delivery_lines',
  gwt_receive_delivery_cache:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_receive_delivery_cache',
  /**
   * purchase orders search
   */
  purchase_orders_search:
    PO_MATCH_SERVICE_URL + '/purchase_order/purchase_orders_search',
  search_suppliers: PO_MATCH_SERVICE_URL + '/purchase_order/suppliers',

  supplie_item_numbers:
    PO_MATCH_SERVICE_URL + '/purchase_order/supplier_item_numbers_map/get_dict',

  supplie_item_numbers_list:
    PO_MATCH_SERVICE_URL + '/purchase_order/supplier_item_numbers_map/list',
  delete_supplie_item_number:
    PO_MATCH_SERVICE_URL +
    '/purchase_order/get_supplier_item_numbers_list/delete/',
  /**
   * get table extraction line item columns
   */
  get_tbex_lineitems_columns: DOC2API_URL + '/extract/get_column_headers',
  /**
   * group by line items
   */
  group_line_items: DOC2API_URL + '/table_extraction/reformat_table',
  /**
   * doc2Landing Document processing api
   */
  process_doc2landing_document:
    DOC2API_URL + '/document/process_doc2landing_document',
  /**
   * import vendors csv
   */
  upload_vendors_csv: DOC2API_URL + '/vendors/import_vendors_csv',
  /**
   * set preference
   */

  add_preferences: DOC2API_URL + '/preferences/set_preferences',
  set_preference: DOC2API_URL + '/preferences/set_preference',
  get_tfidf_preference: DOC2API_URL + '/preferences/get_tfidf_preference',
  set_tfidf_preference: DOC2API_URL + '/preferences/set_tfidf_preference',
  set_cache_preference: DOC2API_URL + '/preferences/set_cache_preference',
  get_cache_preference: DOC2API_URL + '/preferences/get_cache_preference',
  set_grouping_dashboard_preference:
    DOC2API_URL + '/preferences/set_grouping_dashboard_preference',
  set_grouping_purchase_order_preference:
    DOC2API_URL + '/preferences/set_grouping_purchase_order_preference',
  get_warehouse_user_preference:
    DOC2API_URL + '/preferences/get_warehouse_user_preference',
  set_reporting_engine_preference:
    DOC2API_URL + '/preferences/set_reporting_engine_preference',
  set_bod_connection_stream_preference:
    DOC2API_URL + '/preferences/set_bod_connection_stream_preference',
  get_bod_connection_stream_preference:
    DOC2API_URL + '/preferences/get_bod_connection_stream_preference',
  get_db_credentials: DOC2API_URL + '/preferences/get_db_credentials',
  /**
   * get preference
   */
  get_preference: DOC2API_URL + '/preferences/get_preference',

  /**
   * set preference
   */
  set_user_preference: DOC2API_URL + '/preferences/set_user_preference',
  /**
   * get preference
   */
  get_user_preference: DOC2API_URL + '/preferences/get_user_preference',
  /**
   * get user all preferences
   */
  get_user_preferences: DOC2API_URL + '/preferences/get_user_preferences',

  /**
   * get preferences
   */
  get_preferences: DOC2API_URL + '/preferences/get_preferences',
  /**
   * delete preferences
   */
  delete_preferences: DOC2API_URL + '/preferences/remove_preference',
  /**
   * get acl settings
   */
  get_acl_settings: DOC2API_URL + '/acl_updater/get_acl_settings',
  /**
   * get acl settings
   */
  create_acl_initializer: DOC2API_URL + '/acl_updater/create_acl_settings',
  /**
   * get acl settings
   */
  create_acl_mapping: DOC2API_URL + '/acl_updater/create_acl_mapping',
  /**
   * get acl settings
   */
  delete_acl_settings: DOC2API_URL + '/acl_updater/delete_acl_settings',
  /**
   * get acl settings
   */
  delete_acl_mapping: DOC2API_URL + '/acl_updater/delete_acl_mapping',
  /**
   * API for flow2 export
   */
  export_flow2: DOC2API_URL + '/configurations/configure_flow2_export',
  /**
   * API for sftp export
   */
  sftp_export: DOC2API_URL + '/configurations/configure_sftp_export',
  infor_sftp_export:
    DOC2API_URL + '/configurations/configure_infor_sftp_export',
  /**
   * API for smb export
   */
  smb_export: DOC2API_URL + '/configurations/configure_smb_export',
  /**
   * API for watcher export (regular export config with export_method='watcher')
   * Note: This is different from watchdog_configurations which is for true Watchdog configs
   */
  watcher_export: DOC2API_URL + '/export-configurations/watcher',
  /**
   * API to check for File in S3
   */
  s3_check: DOC2API_URL + '/resource/check_S3',
  s3_get_file: DOC2API_URL + '/resource/get_file',
  /**
   * API to add/edit import-Email
   */
  import_mail: IMPORT_EMAIL_URL + '/email_import/add_or_update',

  /**
   * API to add/edit import-Email
   */
  add_update_email: IMPORT_EMAIL_URL + '/email_import/add_or_update',

  /**
   * API to save inbound email setting
   */
  inbound_save: IMPORT_EMAIL_URL + '/email_import/inbound_save',

  /**
   * API to save inbound email setting
   */
  de_activate_inbound:
    IMPORT_EMAIL_URL + '/email_import/toggle_activation_status',

  /**
 /**
 * API to load inbound email setting
 */
  inbound_load: IMPORT_EMAIL_URL + '/email_import/inbound_load',

  /**
   * API to load inbound email setting
   */
  inbound_load_logs: IMPORT_EMAIL_URL + '/email_import/inbound_logs',
  /**
   * API to finish ms/o365 authentication
   */
  import_mail_ms_finish: IMPORT_EMAIL_URL + '/email_import/ms_auth',
  /**
   * API to get the emails Folders
   */
  get_email_folders: IMPORT_EMAIL_URL + '/email_import/get_folders',
  /**
   * API to change email-import-folder
   */
  import_mail_change_folder_and_email:
    IMPORT_EMAIL_URL + '/email_import/change_folder_and_email',
  /**
   * API to get import-Email
   */
  get_import_mail: IMPORT_EMAIL_URL + '/email_import/get_email',
  /**
   * API to delete import-Email
   */
  delete_import_mail: IMPORT_EMAIL_URL + '/email_import/delete',
  /**
   * API to activate/deactivate import-Email
   */
  toggle_email_activation_status:
    IMPORT_EMAIL_URL + '/email_import/toggle_email_activation_status',
  /**
   * API to manually fetch newest E-Mails PDF
   */
  manual_import_mail: IMPORT_EMAIL_URL + '/email_import/fetch_emails',
  /**
   * API to import all E-Mails
   */
  start_all_email_import: IMPORT_EMAIL_URL + '/email_import/import_all_emails',
  /**
   * API to fetch E-Mail Import Log
   */
  fetch_import_log: IMPORT_EMAIL_URL + '/email_import/get_import_log',

  /**
   * GET email import list
   */
  email_import_list: IMPORT_EMAIL_URL + '/email_import/list',
  /**
   * Test Tenant Connection
   */
  test_email_import_credentials:
    IMPORT_EMAIL_URL + '/email_import/test_connection',
  /**
   * API to check login
   */
  check_email_login: IMPORT_EMAIL_URL + '/email_import/test_exsisting_login',
  /**
   * save company information
   */
  set_org_info: DOC2API_URL + '/org_details/update_organisation',
  /**
   * save company information
   */
  get_org_info: DOC2API_URL + '/org_details/get_org_data',
  /**
   * save company information
   */
  set_org_profile: DOC2API_URL + '/org_details/set_org_profile',
  /**
   * save company information
   */
  get_org_profile: DOC2API_URL + '/org_details/get_org_profile',
  /**
   * API for get_items_from_dictionary
   */
  get_items_from_dictionary:
    DOC2API_URL + '/document/get_items_from_dictionary/',
  /**
   * API for custom field
   */
  get_custom_field_labels: DOC2API_URL + '/field/get_custom_field_labels',
  /**
   * API to set fields labels
   */
  set_custom_field_labels: DOC2API_URL + '/field/set_custom_field_labels',

  get_dashboard_templates: DOC2API_URL + '/dashboard_templates/dashboards',

  create_dashboard: DOC2API_URL + '/dashboard_templates/create_dashboard',

  delete_dashboard: DOC2API_URL + '/dashboard_templates/delete_dashboard',

  update_dashboard: DOC2API_URL + '/dashboard_templates/update_dashboard',

  /**
   * API to get document types
   */
  get_document_types: DOC2API_URL + '/document_type/get_document_types',
  /**
   * API to save document type
   */
  create_document_type: DOC2API_URL + '/document_type/create_document_type',
  /**
   * create document field group
   */
  create_document_field_group:
    DOC2API_URL + '/document_type/create_document_field_group',
  /**
   * create document field
   */
  create_document_field: DOC2API_URL + '/document_type/create_document_field',
  /**
   * set extraction type
   */
  set_extraction_type: DOC2API_URL + '/document_type/set_extraction_type',

  get_all_document_type_attributes:
    DOC2API_URL + '/document_type/get_all_document_type_attributes',
  delete_document_type_attribute:
    DOC2API_URL + '/document_type/delete_document_type_attribute',
  get_document_type_attributes:
    DOC2API_URL + '/document_type/get_document_type_attributes',
  set_document_type_attribute:
    DOC2API_URL + '/document_type/set_document_type_attribute',
  /**
   * API to block table extraction for supplier
   */
  block_supplier_table_extraction:
    DOC2API_URL + '/block_table_extraction/blocked_list/add',
  /**
   * API to get list of blocked supplier list
   */
  get_blocked_supplier_list:
    DOC2API_URL + '/block_table_extraction/blocked_list',
  /**
   * DOC2 delete blocked supplier
   */
  delete_blocked_supplier:
    DOC2API_URL + '/block_table_extraction/blocked_list/',
  /**
   * DOC2 delete blocked supplier by supplier id
   */
  delete_by_supplier_id:
    DOC2API_URL + '/block_table_extraction/blocked_list/delete_by_supplier_id/',

  /**
   * doc extraction data to show in external page
   */
  get_table_train_data: DOC2API_URL + '/ephesoft/get_table_train_data?id=',
  /**
   * doc  save extraction data for external page
   */
  save_table_train_data: DOC2API_URL + '/ephesoft/save_extraction_rules/',

  /**
   * reformat_table_table_v3
   */
  reformat_table_table_v3: DOC2API_URL + '/table_extraction/reformat_table',
  /**
   * create_document_table_column
   */
  create_document_table_column:
    DOC2API_URL + '/table_extraction/create_document_table_column',
  /**
   * get_table_text
   */
  get_table_text: DOC2API_URL + '/extract/get_table_text',

  /**
   * get table v3
   */
  get_table_v3: DOC2API_URL + '/table_extraction/get_table',
  /**
   * get auto_extract_table v3
   */
  get_auto_table_v3: DOC2API_URL + '/table_extraction/auto_extract_table',
  /**
   * get get_item_number for article number field for medi
   */
  get_medi_item_number: DOC2API_URL + '/medi/get_item_number',
  /**
   *
   */
  get_item_number_from_doc_id:
    DOC2API_URL + '/medi/get_item_number_from_doc_id',

  /**
   *
   */
  get_medi_item_number_fields_map:
    DOC2API_URL + '/medi/get_medi_item_number_fields_map',

  /**
   *
   */
  get_error_infos: DOC2API_URL + '/resource/get_errors',
  /**
   * Delete Rules
   */
  delete_table_rules: DOC2API_URL + '/table_extraction/delete_table_rules',

  /**
   * doc  save save_rules
   */
  save_rules: DOC2API_URL + '/export/save_extraction_rules',
  /**
   * doc get_ai_score
   */
  get_ai_score: DOC2API_URL + '/extract/get_ai_score',

  /**
   * doc save_special_rules
   */
  save_special_rules: DOC2API_URL + '/special_rules/save',

  /**
   * get PO display columns from api
   */
  get_purchase_order_display_cols:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_purchase_order_display_cols',
  get_all_purchase_order_display_cols:
    PO_MATCH_SERVICE_URL + '/purchase_order/all_purchase_order_display_cols',
  /**
   * convert PO into line item table
   */
  get_table_from_po_numbers:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_table_from_po_numbers',
  /**
   * auto_po_match
   */
  auto_po_match: PO_MATCH_SERVICE_URL + '/purchase_order/auto_po_match',
  auto_po_match_v2: PO_MATCH_SERVICE_URL + '/purchase_order/auto_po_match_v2',
  manual_match_lines:
    PO_MATCH_SERVICE_URL + '/purchase_order/manual_match_lines',
  sync_receive_delivery_for_purchase_orders_by_api:
    DOC2API_URL + '/import/sync_receive_delivery_for_purchase_orders_by_api',
  /**
   * get external app po match data
   */
  get_ext_po_match_data: DOC2API_URL + '/ephesoft/get_po_match_data/',
  /**
   * get_purchase_orders_list
   */
  get_purchase_orders_list:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_purchase_orders_list',
  /**
   * get_tolerance_list
   */
  get_tolerance_list:
    PO_MATCH_SERVICE_URL + '/purchase_order/get_tolerance_list',
  /**
   * set_item_tolerance
   */
  set_item_tolerance:
    PO_MATCH_SERVICE_URL + '/purchase_order/set_item_tolerance',
  /**
   * get_purchase_orders_list
   */
  save_ext_po_data: DOC2API_URL + '/ephesoft/save_po_match_data/',
  /**
   * auto test add update Document
   */
  auto_test_add_update_document: DOC2API_URL + '/auto_test/add_update_document',
  /**
   * get document data
   */
  get_document_data: DOC2API_URL + '/auto_test/get_document_data',
  /**
   * auto process document
   */
  get_document_table_validation_rules:
    DOC2API_URL + '/field/get_document_table_validation_rules',
  /**
   * update_document_table_column
   */
  update_document_table_column:
    DOC2API_URL + '/document_table/update_document_table_column',
  /**
   * get_ocr_data
   */
  get_ocr_data: DOC2API_URL + '/document/get_ocr_data/',
  /**
   * post_data_fellowkv
   */
  post_data_fellowkv: DOC2API_URL + '/fellowkv/post_data',
  /**
   * get_document_type_regexes
   */
  get_document_type_regexes: '/preferences/get_document_type_regexes',
  /**
   * update_document_type_regexes
   */
  update_document_type_regexes: '/preferences/update_document_type_regexes',
  /**
   * get_document_type_regexes
   */
  get_fellow_kv_regexes: '/preferences/get_fellow_kv_regexes',
  /**
   * update_document_type_regexes
   */
  update_fellow_kv_regexes: '/preferences/update_fellow_kv_regexes',
  /**
   * get_organisation_preference
   */
  get_org_pref: DOC2API_URL + '/preferences/get_organisation_preference',

  get_org_pref_v2: DOC2API_URL + '/preferences/get_organisation_preference_v2',
  /**
   * update_organisation_preference
   */
  set_org_pref: DOC2API_URL + '/preferences/update_organisation_preference',
  /**
   * validate_patterns
   */
  validate_patterns:
    DOC2API_URL + '/regex_and_patterns/regex/validate_patterns',
  /**
   * update_document_type_regexes
   */
  update_organisation_ignore_filters:
    '/preferences/update_organisation_ignore_filters',
  /**
   * get next document
   */
  get_next_document: DOC2API_URL + '/document/get_next_document/',

  next_document_v2: DOC2API_URL + '/document/next',
  /**
   * update_configuration_visibility
   */
  update_configuration_visibility:
    DOC2API_URL + '/configurations/update_configuration_visibility',

  /**
   * get_sample_documents
   */
  get_sample_documents_trainer:
    DOC2API_URL + '/document_trainer/get_sample_documents',
  /**
   * add_sample_document
   */
  add_sample_document_trainer:
    DOC2API_URL + '/document_trainer/add_sample_document',

  /**
   * get trained doc count by doc type or all
   */
  get_trained_docs_count:
    DOC2API_URL + '/document_trainer/get_trained_docs_count',
  /**
   * delete_sample_document
   */
  delete_sample_document_trainer:
    DOC2API_URL + '/document_trainer/delete_sample_document',
  /**
   * get_document_field_groups
   */
  get_document_field_groups:
    DOC2API_URL + '/document_type/get_document_field_groups',
  /**
   * delete_document_field_group
   */
  delete_document_field_group:
    DOC2API_URL + '/document_type/delete_document_field_group',

  /**
   * delete_document_field
   */
  delete_document_field: DOC2API_URL + '/document_type/delete_document_field',
  /**
   * get_document_tables
   */
  get_document_tables: DOC2API_URL + '/document_table/get_document_tables',
  /**
   * get_all_document_tables
   */
  get_all_document_tables:
    DOC2API_URL + '/document_table/get_all_document_tables',
  /**
   * get_document_table_columns
   */
  get_document_table_columns:
    DOC2API_URL + '/document_table/get_document_table_columns',
  /**
   * get_document_table_columns
   */
  get_all_document_table_columns:
    DOC2API_URL + '/document_table/get_all_document_table_columns',
  /**
   * delete_document_table
   */
  delete_document_table: DOC2API_URL + '/document_table/delete_document_table',
  /**
   * create_document_table
   */
  create_document_table: DOC2API_URL + '/document_table/create_document_table',

  /**
   * get_document_tables_with_columns
   */
  get_document_tables_with_columns:
    DOC2API_URL + '/document_table/get_document_tables_with_columns',

  /**
   * create_table_column
   */
  create_table_column:
    DOC2API_URL + '/document_table/create_document_table_column', // no doc_id
  /**
   * delete_table_column
   */
  delete_table_column:
    DOC2API_URL + '/document_table/delete_document_table_column',
  /**
   * test_classification
   */
  test_classification: DOC2API_URL + '/document_trainer/test_classification',
  /**
   * retrain_model
   */
  retrain_model: DOC2API_URL + '/document_trainer/retrain_model',
  /**
   * approve_or_reject_document
   */
  approve_or_reject_document: DOC2API_URL + '/document/approve_or_reject/',
  /**
   * set_status
   */
  set_document_status: DOC2API_URL + '/document/set_status/',

  /**
   * document log
   */
  get_document_log: DOC2API_URL + '/document/get_document_log/',
  /**
   * update_document_log
   */
  update_document_log: DOC2API_URL + '/document/update_document_log/',
  /**
   * get_supported_barcode_types
   */
  get_supported_barcode_types:
    DOC2API_URL + '/preferences/get_supported_barcode_types',
  /**
   * google oauth2
   */
  google_oauth2_endpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
  /**
   * linkedin oauth2
   */
  linkedin_oauth2_endpoint: 'https://www.linkedin.com/oauth/v2/authorization',
  /**
   * get linkedin token
   */
  get_linkedin_token: 'https://www.linkedin.com/oauth/v2/accessToken',
  /**
   * get social token
   */
  social_token: AUTH_URL + '/social/token',
  /**
   * microsoft oauth2
   */
  micorsoft_oauth2_endpoint:
    'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',

  /**
   * resend_confirmation_email
   */
  resend_confirmation_email: AUTH_URL + '/user/resend_confirmation_email',

  /**
   * confirm_email
   */
  confirm_email: AUTH_URL + '/user/confirm_email',

  get_purchase_order_distributed_charges:
    PO_MATCH_SERVICE_URL +
    '/purchase_order/get_purchase_order_distributed_charges',

  delete_cache: DOC2API_URL + '/preferences/delete_cache_for_org',
  /**
   * get user login history (current user)
   */
  get_user_login_history: AUTH_URL + '/api/v1/users/me/login-history',
  /**
   * get user login history by user ID (requires admin)
   */
  get_user_login_history_by_id:
    AUTH_URL + '/api/v1/users/{user_id}/login-history',
  /**
   * get organization login analytics (aggregated login stats)
   */
  get_login_analytics: AUTH_URL + '/api/v1/login-analytics',
  /**
   * create new workflow instance
   */
  workflow_get_settings: TERRAFORM_URL + '/settings/all',
  terraform_get_settings: TERRAFORM_URL + '/settings/all',
  // trigger new workflow instance creation for user
  workflow_actions: TERRAFORM_URL + '/webhook/workflow/actions',

  insight_actions: TERRAFORM_URL + '/webhook/insight/actions',
  // endpoint to send email for forget password
  forgot_password_url: AUTH_URL + '/oauth2/reset_password_request_doc2app',

  forget_password: AUTH_URL + '/user/send_forget_password_email',
  // endpoint for reset password
  reset_password: AUTH_URL + '/user/reset_password_from_token',

  // endpoint send welcome email on app turn on
  apps_welcome_email: AUTH_URL + '/user/send_app_welcome_email',

  // get number of days remaining for subscription expiry
  remaining_days: AUTH_URL + '/user/get_days_remaining',

  organization_subscriptions:
    AUTH_URL + '/organisation/subscriptions?include_token=false',
  monthly_organisation_metric: AUTH_URL + '/organisation/monthly_metric',

  delete_document_type: DOC2API_URL + '/document_type/delete_document_type/',

  activate_deactivate_document_type:
    DOC2API_URL + '/document_type/activate_deactivate_document_type',

  document_lock_acquire: DOC2API_URL + '/document_lock/acquire_lock',
  document_release_lock: DOC2API_URL + '/document_lock/release_lock',

  get_subscriptions_usage_statistics:
    AUTH_URL + '/app_usage_log/get_usage_statistics',
  insight_url: INSIGHT_URL,

  create_insight_org: AUTH_URL + '/insight/create',

  /**
   * set_session_user - Using v2 API endpoint
   */
  set_session_user: AUTH_URL + '/v2/set_session_user',

  health_check: DOC2API_URL + '/api/health/services',
  support_ticket: CLOUD_INTEGRATION_URL + '/doc/docbits-getting-started',

  get_all_groups_permissions:
    DOC2API_URL + '/group_permission/get_all_groups_permissions',
  set_all_groups_permissions:
    DOC2API_URL + '/group_permission/set_all_groups_permissions',

  roles_group_delete: DOC2API_URL + '/groups/delete/',
  roles_group_create: DOC2API_URL + '/groups/create',
  get_groups: DOC2API_URL + '/groups/get_groups',
  roles_group_edit: DOC2API_URL + '/groups/update/',
  get_group_users: DOC2API_URL + '/groups_and_users/get_group_users',
  get_user_groups: DOC2API_URL + '/groups_and_users/get_user_groups',
  remove_user_from_group:
    DOC2API_URL + '/groups_and_users/remove_user_from_group',
  add_user_to_group: DOC2API_URL + '/groups_and_users/add_user_to_group',
  get_user_groups_and_permissions:
    DOC2API_URL + '/group_permission/get_user_groups_and_permissions',
  get_doc_type_permissions:
    DOC2API_URL + '/access_controls/get_doc_type_permissions',
  get_all_document_types_settings:
    DOC2API_URL + '/access_controls/all_doctype_settings',
  update_all_document_types_settings:
    DOC2API_URL + '/access_controls/update_all_from_json',
  get_document_type_all_field:
    DOC2API_URL + '/access_controls/all_doctype_field_settings',

  user_organisation:
    DOC2API_URL +
    '/sub_organisation_user/get_user_organisation_and_sub_organisation',

  get_sub_organisations:
    DOC2API_URL + '/sub_organisations/get_sub_organisations',

  create_sub_organisations: DOC2API_URL + '/sub_organisations/create',

  delete_sub_organisations: DOC2API_URL + '/sub_organisations/delete',
  edit_sub_organisations: DOC2API_URL + '/sub_organisations/update',
  get_sub_organisation_users:
    DOC2API_URL + '/sub_organisation_user/get_sub_organisation_users',

  remove_user_from_sub_organisation:
    DOC2API_URL + '/sub_organisation_user/remove_user_from_sub_organisation',
  add_user_to_sub_organisation:
    DOC2API_URL + '/sub_organisation_user/add_user_to_sub_organisation',

  get_sub_document_types: DOC2API_URL + '/document_type/get_sub_document_types',

  delete_sub_document_type:
    DOC2API_URL + '/document_type/delete_sub_document_type',

  get_formatted_amount: DOC2API_URL + '/locale/format_amount',
  create_sub_document_type:
    DOC2API_URL + '/document_type/create_sub_document_type',
  create_copy_sub_document_type:
    DOC2API_URL + '/document_type/clone_sub_document_type',
  edit_sub_document_type:
    DOC2API_URL + '/document_type/update_sub_document_type',
  get_document_types_and_sub_types:
    DOC2API_URL + '/document_type/get_document_types_and_sub_types',

  get_document_status_alerts:
    DOC2API_URL + '/document_status_alert/get_document_status_alerts',
  toggle_document_status_alert:
    DOC2API_URL + '/document_status_alert/toggle_document_status_alert',

  remove_document_status_alert:
    DOC2API_URL + '/document_status_alert/remove_document_status_alert',

  create_document_status_alert:
    DOC2API_URL + '/document_status_alert/create_document_status_alert',
  update_document_status_alert:
    DOC2API_URL + '/document_status_alert/update_document_status_alert',
  get_document_status_alert_statuses:
    DOC2API_URL + '/document_status_alert/get_document_status_alert_statuses',
  get_document_status_alert_logs: DOC2API_URL + '/document_status_alert/logs/',

  split_document: DOC2API_URL + '/document/split',

  get_flow_data: DOC2API_URL + '/flow/get_document_flow/',

  get_flow_data_org: DOC2API_URL + '/flow/get_documents_flow/',

  get_flow_data_system_avg: DOC2API_URL + '/flow/get_documents_flow_system_avg/',

  clear_flow_until_restart: DOC2API_URL + '/flow/clear_until_restart/',

  /**
   * change assignment of documents
   */
  assign_documents: DOC2API_URL + '/document/assign',

  /**
   * get user sub orgs along with users
   */
  get_sub_organisations_with_users:
    DOC2API_URL + '/sub_organisation_user/get_sub_organisations_with_users',

  get_doc_classification_rules:
    DOC2API_URL + '/doc_classification_rules/get_doc_classification_rules',

  // E-Invoice validation rules catalog (BOL-414/417)
  einvoice_validation_rules:
    DOC2API_URL + '/electronic_document/validation_rules',
  einvoice_validation_rule_severity:
    DOC2API_URL + '/electronic_document/validation_rules', // append /{code}/severity
  // E-Invoice acceptance policy registry (BOL-429)
  einvoice_validation_versions:
    DOC2API_URL + '/electronic_document/validation_versions',
  // E-Invoice DocNet notification routing (BOL-430/431)
  einvoice_notification_agents:
    DOC2API_URL + '/electronic_document/notification_agents',
  einvoice_notification_routes:
    DOC2API_URL + '/electronic_document/notification_routes',

  // E-Invoice validation policy wizard (BOL-439/440)
  einvoice_wizard_definitions:
    DOC2API_URL + '/electronic_document/policy/wizard/definitions',
  einvoice_wizard_state:
    DOC2API_URL + '/electronic_document/policy/wizard/state',
  einvoice_wizard_apply:
    DOC2API_URL + '/electronic_document/policy/wizard/apply',

  create_doc_classification_rule:
    DOC2API_URL + '/doc_classification_rules/create',

  delete_doc_classification_rule:
    DOC2API_URL + '/doc_classification_rules/delete',

  update_doc_classification_rule:
    DOC2API_URL + '/doc_classification_rules/update',

  get_additional_attributes:
    DOC2API_URL +
    '/document_field_and_table/additional_attribute/get_additional_attributes',
  set_table_additional_attribute:
    DOC2API_URL +
    '/document_field_and_table/additional_attribute/table_column/set_additional_attribute',
  get_lookup_lov: DOC2API_URL + '/master_data_lookup/get_lookup_lov/',
  get_master_data_lookup: DOC2API_URL + '/master_data_lookup/get_data',
  get_master_data_download_csv:
    DOC2API_URL + '/master_data_lookup/download_csv',
  get_master_dataset_types:
    DOC2API_URL + '/master_data_lookup/get_dataset_types',
  get_master_dataset_fields:
    DOC2API_URL + '/master_data_lookup/get_dataset_fields',
  get_all_data_fields_alias:
    DOC2API_URL + '/master_data_lookup/get_data_fields_aliases',
  get_grouped_data_set_types:
    DOC2API_URL + '/master_data_lookup/get_grouped_data_set_types',

  test_endpoint_response:
    DOC2API_URL + '/master_data_lookup/rest_api_lookup/test_response',
  test_data_extraction:
    DOC2API_URL + '/master_data_lookup/rest_api_lookup/test_data_extraction',
  create_endpoint: DOC2API_URL + '/master_data_lookup/rest_api_lookup/create',
  update_endpoint: DOC2API_URL + '/master_data_lookup/rest_api_lookup/update',
  get_master_data_list:
    DOC2API_URL + '/master_data_lookup/rest_api_lookup/list',
  delete_custom_record:
    DOC2API_URL + '/master_data_lookup/rest_api_lookup/delete',
  trigger_custom_record:
    DOC2API_URL + '/master_data_lookup/rest_api_lookup/trigger',

  get_master_data_type_alias:
    DOC2API_URL + '/master_data_lookup/get_dataset_types_alias',
  post_master_data_set_type_alias:
    DOC2API_URL + '/master_data_lookup/set_dataset_types_alias',

  import_master_data: DOC2API_URL + '/master_data_lookup/import_data',
  post_master_data_create_record:
    DOC2API_URL + '/master_data_lookup/create_records',
  post_master_data_update_record:
    DOC2API_URL + '/master_data_lookup/create_or_update_records',
  post_master_data_delete_record:
    DOC2API_URL + '/master_data_lookup/delete_record',
  post_master_data_update_bulk_record:
    DOC2API_URL + '/master_data_lookup/update_records_attributes',

  get_master_data_lookup_data_v2:
    DOC2API_URL + '/master_data_lookup/get_data_v2',
  get_supplier_names:
    DOC2API_URL + '/master_data_lookup/supplier_names',
  get_master_data_lookup_asn_dashboard:
    DOC2API_URL + '/asn_dashboard/get_list',
  get_master_data_lookup_asn_dashboard_detail:
    DOC2API_URL + '/asn_dashboard/get_details/',
  get_asn_order_preview: DOC2API_URL + '/asn_dashboard/get_asn_preview/',
  get_asn_share_tokens: DOC2API_URL + '/asn_dashboard/share/get_details',
  get_master_data_lookup_asn_dashboard_export:
    DOC2API_URL + '/asn_dashboard/export_pdf/',
  create_layout_templates: DOC2API_URL + '/document_layout_template/create',
  get_layout_templates:
    DOC2API_URL + '/document_layout_template',
  get_all_layout_templates:
    DOC2API_URL + '/document_layout_template/all',
  get_document_layout_template_by_id:
    DOC2API_URL + '/document_layout_template/by-id/',
  document_layout_template_by_id: DOC2API_URL + '/document_layout_template/by-id/',
  upload_template_sample:
    DOC2API_URL + '/document_layout_template/upload_template_sample',
  apply_default_to_origins: 
    DOC2API_URL + '/document_layout_template/apply_default_to_origins',


  custom_model_list: DOC2API_URL + '/custom_model/list',
  custom_model_label: DOC2API_URL + '/custom_model_label/list',
  custom_model_update: DOC2API_URL + '/custom_model',
  custom_model_label_update: DOC2API_URL + '/custom_model_label',

  // REMOVED: create_log: DOC2API_URL + '/logs/create', - API endpoint does not exist

  get_extraction_attributes:
    DOC2API_URL + '/electronic_document/get_extraction_attributes',
  get_extraction_attribute_value_versions:
    DOC2API_URL +
    '/electronic_document/get_extraction_attribute_value_versions',
  create_extraction_attribute_value_draft:
    DOC2API_URL +
    '/electronic_document/create_extraction_attribute_value_draft',
  activate_extraction_attribute_value_draft:
    DOC2API_URL +
    '/electronic_document/activate_extraction_attribute_value_draft',
  update_extraction_attribute_value_draft:
    DOC2API_URL +
    '/electronic_document/update_extraction_attribute_value_draft',
  delete_extraction_attribute_value_draft:
    DOC2API_URL +
    '/electronic_document/delete_extraction_attribute_value_draft',
  create_extraction_attribute:
    DOC2API_URL + '/electronic_document/create_extraction_attribute',
  delete_extraction_attribute:
    DOC2API_URL + '/electronic_document/delete_extraction_attribute',
  create_extraction_attribute_value_draft_from:
    DOC2API_URL +
    '/electronic_document/create_extraction_attribute_value_draft_from',
  get_preview_output: DOC2API_URL + '/electronic_document/preview_output',

  get_document_type_script: DOC2API_URL + '/document_script/doc_type/',

  test_document_type_script: DOC2API_URL + '/document_script/test',

  get_fields_permissions:
    DOC2API_URL + '/access_controls/get_users_doctype_permissions',

  update_document_type_script: DOC2API_URL + '/document_script',

  document_clone_script: DOC2API_URL + '/document_script/clone',

  delete_document_script: DOC2API_URL + '/document_script/by-id/',

  get_script_by_id: DOC2API_URL + '/document_script/by-id/',

  script_activate_deactivate:
    DOC2API_URL + '/document_script/activate_deactivate/',

  mark_as_active: DOC2API_URL + '/document_script/activate',

  get_script_versions: DOC2API_URL + '/document_script/get_script_versions/',

  create_script_draft: DOC2API_URL + '/document_script/draft',

  delete_script_draft: DOC2API_URL + '/document_script/delete_script_draft/',

  restore_version: DOC2API_URL + '/document_script/restore_version/',

  get_next_model_training_schedule:
    DOC2API_URL + '/document_trainer/get_next_model_training_schedule',

  auto_accounting_dashboard:
    DOC2API_URL + '/document/accounting/dashboard',
  /**
   * Reassign Group to Fields
   */
  assign_group_to_fields: DOC2API_URL + '/document_type/assign_group_to_fields',

  get_pending_tasks_count:
    DOC2API_URL + '/celery_tasks_manager/get_queued_tasks_count',
  get_queued_tasks_count:
    DOC2API_URL + '/celery_tasks_manager/get_queued_tasks_count',

  /* API to import all tasks info
   */
  task_management_table: DOC2API_URL + '/celery_log/celery_beat_status',
  /**
   * API to retrigger one task
   */
  retrigger_task: DOC2API_URL + '/celery_log/retrigger_task',
  /**
   * Data Synchronisation
   */
  data_synchronization: DOC2API_URL + '/celery_log/data_synchronization',
  /**
   * Tasks and Notifications
   */
  api_task: DOC2API_URL + '/tasks_and_notifications/tasks',
  api_task_cache: DOC2API_URL + '/tasks_and_notifications/tasks_caches/',
  api_total_tasks_count:
    DOC2API_URL + '/tasks_and_notifications/tasks_total_count',
  notifications: DOC2API_URL + '/tasks_and_notifications/notifications/',
  /**
   * List of Values
   */
  get_lov_list: DOC2API_URL + '/list_of_values/lov_list',
  create_lov_type: DOC2API_URL + '/list_of_values/create_lov_type',
  delete_lov_type: DOC2API_URL + '/list_of_values/delete_lov_type',
  import_lov: DOC2API_URL + '/list_of_values/import_data',
  update_fields:
    DOC2API_URL +
    '/lookup_configurations/lookup_configuration/:lookup_id/fields/:field_id',
  create_config_field:
    DOC2API_URL + '/lookup_configurations/lookup_configuration/:id/fields',
  duplicate_config:
    DOC2API_URL + '/lookup_configurations/lookup_configuration/:id/duplicate',
  toggle_config_activation_status:
    DOC2API_URL +
    '/lookup_configurations/lookup_configuration/:id/toggle_activation_status',
  create_lookup_configuration:
    DOC2API_URL + '/lookup_configurations/lookup_configuration',
  update_lookup_configuration:
    DOC2API_URL + '/lookup_configurations/lookup_configuration/by-id/:id',
  lookup_configurations_fields:
    DOC2API_URL + '/lookup_configurations/lookup_configuration/:id/fields',
  lookup_configurations:
    DOC2API_URL + '/lookup_configurations/lookup_configuration',

  lookup_lov_configuration:
    DOC2API_URL + '/lookup_configurations/lookup_lov_configuration',
  get_lookup_lov_configuration:
    DOC2API_URL + '/lookup_configurations/lookup_lov_configuration',
  lov_duplicate_config:
    DOC2API_URL +
    '/lookup_configurations/lookup_lov_configuration/:id/duplicate',
  toggle_lov_config_activation:
    DOC2API_URL +
    '/lookup_configurations/lookup_lov_configuration/:id/toggle_activation_status',
  lov_lookup_configurations_fields:
    DOC2API_URL + '/lookup_configurations/lookup_lov_configuration/:id/fields',

  get_list_of_values: DOC2API_URL + '/list_of_values/get_list_of_values',
  get_lov_value_by_parent_org:
    DOC2API_URL + '/list_of_values/get_multiple_list_of_values_by_parent',
  create_lov_value: DOC2API_URL + '/list_of_values/create_lov_value',
  create_lov_values: DOC2API_URL + '/list_of_values/create_lov_values',
  update_lov_values: DOC2API_URL + '/list_of_values/update_lov_values',
  delete_lov_value: DOC2API_URL + '/list_of_values/delete_lov_value',
  duplicate_lov_value: DOC2API_URL + '/list_of_values/duplicate_lov_value',
  update_lov_value: DOC2API_URL + '/list_of_values/update_lov_value',
  get_multiple_list_of_values:
    DOC2API_URL + '/list_of_values/get_multiple_list_of_values',

  get_document_type_filter_keys:
    DOC2API_URL + '/document/filters/document-types',

  apply_script: DOC2API_URL + '/document/script/apply',

  get_document_report: DOC2API_URL + '/reports/get_report',
  get_report_as_xlsx: DOC2API_URL + '/reports/get_report_as_xlsx',
  get_ai_graphical_report: DOC2API_URL + '/reports/get_ai_graphical_report',
  get_report_request: DOC2API_URL + '/reports/get_report_request',
  get_private_report_requests_by_user:
    DOC2API_URL + '/reports/get_private_report_requests_by_user',
  get_public_report_requests_by_org:
    DOC2API_URL + '/reports/get_public_report_requests_by_org',
  get_all_report_requests_by_org:
    DOC2API_URL + '/reports/get_all_report_requests_by_org',
  create_report_request: DOC2API_URL + '/reports/create_report_request',
  edit_report_request: DOC2API_URL + '/reports/update_report_request',
  delete_report_request: DOC2API_URL + '/reports/delete_report_request',

  get_formatted_date: DOC2API_URL + '/locale/format_date',

  reformat_ai_table: DOC2API_URL + '/table_extraction/reformat_ai_table',

  ftp_import_list: IMPORT_FTP_URL + '/ftp_import/list',
  ftp_import_topology: IMPORT_FTP_URL + '/ftp_import/topology',
  ftp_import_de_activate: IMPORT_FTP_URL + '/ftp_import/(de)-activate',
  ftp_import_delete: IMPORT_FTP_URL + '/ftp_import/delete',
  ftp_import_test_connection: IMPORT_FTP_URL + '/ftp_import/test_connection',
  ftp_import_add_or_update: IMPORT_FTP_URL + '/ftp_import/add_or_update',
  get_ftp_folders: IMPORT_FTP_URL + '/ftp_import/get_folders',
  get_ftp_connection_logs: IMPORT_FTP_URL + '/logs/connection_log',
  get_import_logs: IMPORT_EMAIL_URL + '/email_import/import_log',
  get_email_connection_logs: IMPORT_EMAIL_URL + '/logs/connection_log',

  create_support_ticket: DOC2API_URL + '/support/create_support_ticket',

  version: DOC2API_URL + '/version',
  service_versions: DOC2API_URL + '/service_versions',
  set_status_to_ready_for_validation:
    DOC2API_URL + '/document/set_status_to_ready_for_validation',

  // workflows
  // save_workflow_json: DOCFLOW_URL + '/full/save',
  get_workflow_json: DOCFLOW_URL + '/full/workflow',
  // workflow_create: DOCFLOW_URL + '/workflow/create',
  // workflow_update: DOCFLOW_URL + '/workflow/update',
  // workflow_delete: DOCFLOW_URL + '/workflow/delete',
  get_workflow_by_id: DOCFLOW_URL + '/workflow/get/',
  workflow_dashboard: DOCFLOW_URL + '/v3/workflow/dashboard',
  load_document_triggers: DOCFLOW_URL + '/workflow/triggers',
  load_document_statuses: DOCFLOW_URL + '/workflow/document_statuses',
  workflow_when: DOCFLOW_URL + '/v3/cards/when',
  workflow_and: DOCFLOW_URL + '/v3/cards/and',
  workflow_then: DOCFLOW_URL + '/v3/cards/then',
  workflow_all_cards: DOCFLOW_URL + '/v3/cards/all',
  save_workflow_json: DOCFLOW_URL + '/v3/general/save',
  workflow_create: DOCFLOW_URL + '/v3/general/create',
  workflow_update: DOCFLOW_URL + '/v3/general/update',
  workflow_delete: DOCFLOW_URL + '/v3/general/delete/',
  workflow_bulk_delete: DOCFLOW_URL + '/v3/general/bulk_delete',
  workflow_bulk_toggle: DOCFLOW_URL + '/v3/general/bulk_toggle',
  copy_workflow: DOCFLOW_URL + '/v3/general/copy_workflow/',
  workflow_load: DOCFLOW_URL + '/v3/general/load',
  trigger_workflow: DOCFLOW_URL + '/v3/triggers/start_workflows_for_doc',
  get_card_template: DOCFLOW_URL + '/v3/cards/card_template/',
  trigger_workflow_field_change: DOCFLOW_URL + '/v3/triggers/run_workflow',
  get_workflow_logs: DOCFLOW_URL + '/v3/logs/workflow_logs/',
  get_export_workflow_to_json:
    DOCFLOW_URL + '/v3/general/export_workflow_to_json/',
  post_import_workflow_json:
    DOCFLOW_URL + '/v3/general/restore_workflow_from_json',
  license_status: DOCFLOW_URL + '/v3/license/status',
  license_toggle: DOCFLOW_URL + '/v3/license/toggle',
  docflow_version: DOCFLOW_URL + '/version',
  get_workflows_for_org: DOCFLOW_URL + '/v3/general/get_workflows',
  run_workflow_card: DOCFLOW_URL + '/v3/triggers/run_card',

  // Advanced workflow endpoints
  advanced_save: DOCFLOW_URL + '/v3/advanced/save',
  advanced_load: DOCFLOW_URL + '/v3/advanced/load/',
  advanced_validate: DOCFLOW_URL + '/v3/advanced/validate',
  advanced_convert: DOCFLOW_URL + '/v3/advanced/convert_from_standard/',
  advanced_test: DOCFLOW_URL + '/v3/advanced/test',
  advanced_save_as_template: DOCFLOW_URL + '/v3/advanced/save_as_template',
  advanced_templates: DOCFLOW_URL + '/v3/advanced/templates',
  advanced_create_from_template: DOCFLOW_URL + '/v3/advanced/create_from_template/',

  // Workflow variables (org-scoped)
  workflow_variables: DOCFLOW_URL + '/v3/workflow-variables',

  save_mapping_file: DOCFLOW_URL + '/general/save_mapping_file',
  load_mapping_file: DOCFLOW_URL + '/general/load_mapping_file',

  // DocFlow templates
  load_wf_template: DOCFLOW_URL + '/templates/load',
  wf_save_template: DOCFLOW_URL + '/templates/save',
  delete_wf_template: DOCFLOW_URL + '/templates/delete/',
  workflow_template_dashboard: DOCFLOW_URL + '/templates/dashboard',
  copy_workflow_template: DOCFLOW_URL + '/templates/copy_template/',

  // DocFlowTestManager And Workflow Dashboard
  // Old endpoints
  // workflow_logs_recent: DOCFLOW_URL + '/v3/logs/recent',
  // Updated Endpoints
  create_workflowtest_scenario: DOCFLOW_URL + '/test_manager/create_scenario/',
  edit_workflowtest_scenario: DOCFLOW_URL + '/test_manager/edit/',
  delete_workflowtest_scenario: DOCFLOW_URL + '/test_manager/delete/',
  get_workflow_test_scenarios: DOCFLOW_URL + '/test_manager/test_scenarios',
  run_wf_test_by_id: DOCFLOW_URL + '/test_manager/run_test/',
  toggle_activate_test: DOCFLOW_URL + '/test_manager/toggle_activate_test/',
  run_all_tests: DOCFLOW_URL + '/test_manager/run_all_tests',
  workflow_creation_summary: DOCFLOW_URL + '/v3/workflow/creation_summary',
  workflow_dashboard_summary: DOCFLOW_URL + '/v3/logs/summary',

  // locale
  get_formatted_amounts: DOC2API_URL + '/locale/format_amounts',

  // Auto Accounting
  auto_detect_accounts: DOC2API_URL + '/auto_accounting/auto_detect_accounts',
  accounting_delete_training: DOC2API_URL + '/auto_accounting/delete_training',
  accounting_train_model: DOC2API_URL + '/auto_accounting/model_train',

  find_duplicates: DOC2API_URL + '/document/find_duplicates',
  get_export_preview_json: DOC2API_URL + '/export/get_export_preview_json',
  fetch_export_history: DOC2API_URL + '/document/exports/logs',
  get_infor_export_api_json: DOC2API_URL + '/export/get_infor_export_api_json',


  delete_purchase_order_rule: PO_MATCH_SERVICE_URL + '/purchase_order_rules/{value_id}',
  clone_purchase_order_rules_as_draft: PO_MATCH_SERVICE_URL + '/purchase_order_rules/clone_as_draft',
  update_document_type_attribute_value: PO_MATCH_SERVICE_URL + '/purchase_order_rules/{value_id}/save',
  reset_purchase_order_rules_to_default: PO_MATCH_SERVICE_URL + '/purchase_order_rules/reset_to_default',
  approve_document_type_attribute_value: PO_MATCH_SERVICE_URL + '/purchase_order_rules/{value_id}/approve',
  get_all_purchase_order_rules_revisions: PO_MATCH_SERVICE_URL + '/purchase_order_rules/get_all_revisions',
  get_active_purchase_order_rules_revision: PO_MATCH_SERVICE_URL + '/purchase_order_rules/get_active_revision',
  document_type_attribute_value_toggle_active: PO_MATCH_SERVICE_URL + '/purchase_order_rules/{value_id}/toggle-active',


  /**
   * END List of Values
   */

  /**
   * END List of Values
   */

  // ________                ________ .____    .__       .__     __
  // \______ \   ____   ____ \_____  \|    |   |__| ____ |  |___/  |_
  //  |    |  \ /  _ \_/ ___\ /  ____/|    |   |  |/ ___\|  |  \   __\
  //  |    `   (  <_> )  \___/       \|    |___|  / /_/  >   Y  \  |
  // /_______  /\____/ \___  >_______ \_______ \__\___  /|___|  /__|
  //         \/            \/        \/       \/ /_____/      \/

  all_documents: DOC2API_URL + '/doc2light/all_docs',
  dashboard: DOC2API_URL + '/doc2light/dashboard',
  get_doc: DOC2API_URL + '/doc2light/get_doc/',
  create_doc: DOC2API_URL + '/doc2light/create_doc',
  split_create_doc: DOC2API_URL + '/doc2light/split_and_upload_document',
  split_old_doc: DOC2API_URL + '/doc2light/split_old_document',
  delete_doc: DOC2API_URL + '/doc2light/delete_doc',
  download_doc: DOC2API_URL + '/doc2light/download_doc',
  get_doc_text: DOC2API_URL + '/doc2light/get_doc_text',
  get_doc_thumbnail: DOC2API_URL + '/doc2light/thumbnail/',
  get_thumbnail_file: DOC2API_URL + '/doc2light/thumbnail_file_resp/',
  update_doc: DOC2API_URL + '/doc2light/update_doc/',

  get_all_doctypes: DOC2API_URL + '/doc2light/get_doctypes',
  get_doctype: DOC2API_URL + '/doc2light/get_doctype',
  create_doctype: DOC2API_URL + '/doc2light/create_doc_type',
  update_doctype: DOC2API_URL + '/doc2light/update_doctype',
  delete_doctype: DOC2API_URL + '/doc2light/delete_doc_type',

  get_all_tags: DOC2API_URL + '/doc2light/all_tags',
  get_tag: DOC2API_URL + '/doc2light/get_tag/',
  create_tag: DOC2API_URL + '/doc2light/create_tag',
  update_tag: DOC2API_URL + '/doc2light/update_tag',
  delete_tag: DOC2API_URL + '/doc2light/delete_tag',
  remove_tag_from_doc: DOC2API_URL + '/doc2light/remove_tag_from_doc',

  get_all_correspondents: DOC2API_URL + '/doc2light/all_correspondents',
  get_correspondent: DOC2API_URL + '/doc2light/get_correspondent_by_id',
  create_correspondent: DOC2API_URL + '/doc2light/create_correspondent',
  update_correspondent: DOC2API_URL + '/doc2light/update_correspondent',
  delete_correspondent: DOC2API_URL + '/doc2light/delete_correspondent',

  get_all_correspondent_groups:
    DOC2API_URL + '/doc2light/get_correspondent_groups',
  get_correspondent_group: DOC2API_URL + '/doc2light/get_correspondent_group',
  create_correspondent_group:
    DOC2API_URL + '/doc2light/create_correspondent_group',
  rename_correspondent_group:
    DOC2API_URL + '/doc2light/rename_correspondent_group',
  delete_correspondent_group:
    DOC2API_URL + '/doc2light/delete_correspondent_group',
  add_correspondent_to_group:
    DOC2API_URL + '/doc2light/add_correspondent_to_group',
  remove_correspondent_from_group:
    DOC2API_URL + '/doc2light/remove_correspondent_from_group',

  get_path_dashboard: DOC2API_URL + '/doc2light/get_path_dashboard',
  remove_path_condition_pair:
    DOC2API_URL + '/doc2light/delete_pathdirectory_condition_pair_by_ids',
  edit_pathdirectory: DOC2API_URL + '/doc2light/edit_pathdirectory',
  create_path_condition_pair:
    DOC2API_URL + '/doc2light/create_pathdirectory_condition_pair_with_values',
  create_pathdirectory: DOC2API_URL + '/doc2light/create_pathdirectory',
  delete_pathdirectory: DOC2API_URL + '/doc2light/delete_pathdirectory',

  ms_auth: DOC2API_URL + '/doc2light/ms_auth',
  get_accessable_user_stores:
    DOC2API_URL + '/doc2light/get_accessable_user_stores',
  get_store_content: DOC2API_URL + '/doc2light/get_files',
  import_files: DOC2API_URL + '/doc2light/import_files',
  download_files: DOC2API_URL + '/doc2light/download_files',
  upload_files: DOC2API_URL + '/doc2light/upload_files',
  get_folders: DOC2API_URL + '/doc2light/get_folders',
  edit_store: DOC2API_URL + '/doc2light/edit_store',
  delete_store: DOC2API_URL + '/doc2light/delete_store',
  init_auth: DOC2API_URL + '/doc2light/init_auth',
  toggle_store_publicity: DOC2API_URL + '/doc2light/toggle_store_publicity',
  rename_store: DOC2API_URL + '/doc2light/rename_store',
  ocr_upload_to_store: DOC2API_URL + '/doc2light/ocr_and_upload_to_store',
  split_ocr_upload: DOC2API_URL + '/doc2light/split_ocr_and_upload_to_store',
  get_directscan_documents: DOC2API_URL + '/doc2light/get_directscan_documents',
  add_update_storepath: DOC2API_URL + '/doc2light/add_update_storepath',
  get_paths_for_org: DOC2API_URL + '/doc2light/get_paths_for_org',

  supplier_statistic: DOC2API_URL + '/clickhouse/supplier_statistics',

  get_organisation_logo: DOC2API_URL + '/supplier/logo',
  // supplier layout
  get_supplier_layout_document_type:
    DOC2API_URL + '/supplier_layout/document_types',
  create_supplier_layout_document_type:
    DOC2API_URL + '/supplier_layout/create_document_type',
  get_suppier_layout_form: DOC2API_URL + '/supplier_layout/by-id/',
  use_default_layout: DOC2API_URL + '/supplier_layout/restore/',
  submit_supplier_form: DOC2API_URL + '/supplier_layout/',

  // Alfred Small
  get_org_store: DOC2API_URL + '/doc2light/get_org_store',

  save_decision_table: DOC2API_URL + '/decision_trees/create',
  get_decision_tables: DOC2API_URL + '/decision_trees/get_all',
  delete_decision_table: DOC2API_URL + '/decision_trees/delete',
  update_decision_table: DOC2API_URL + '/decision_trees/update',
  get_document_type_fields:
    DOC2API_URL + '/decision_trees/get_document_type_fields',
  export_decision_tree: DOC2API_URL + '/decision_trees/export',
  get_decision_table_by_id: DOC2API_URL + '/decision_trees/get_by_id',
  import_decision_tabel: DOC2API_URL + '/decision_trees/import',
  test_decision_table: DOC2API_URL + '/decision_trees/test',

  // supplier export config
  supplier_export_config: DOC2API_URL + '/supplier_export/download_api_config',
  get_supplier_export_config: DOC2API_URL + '/supplier_export/get_config',
  save_supplier_export_config: DOC2API_URL + '/supplier_export/save_config',
  use_default_export_config:
    DOC2API_URL + '/supplier_export/save_default_create_supplier_config',

  //  access control
  access_control_details:
    DOC2API_URL + '/access_controls/all_doctype_field_settings',
  access_control_update: DOC2API_URL + '/access_controls/update_all_from_json',

  // supplier calculations
  create_calculation: DOC2API_URL + '/supplier_calculations/create',
  delete_calculation: DOC2API_URL + '/supplier_calculations/delete',
  get_calculations: DOC2API_URL + '/supplier_calculations/get_calculations',

  // invoice calculation - discount mapping
  discount_mapping_upsert:
    DOC2API_URL + '/invoice_calculation/discount_mapping/upsert',
  discount_mapping_list:
    DOC2API_URL + '/invoice_calculation/discount_mapping/list',
  discount_mapping_delete:
    DOC2API_URL + '/invoice_calculation/discount_mapping/delete',
  // invoice calculation - payment term mapping
  payment_mapping_upsert:
    DOC2API_URL + '/invoice_calculation/payment_mapping/upsert',
  payment_mapping_list:
    DOC2API_URL + '/invoice_calculation/payment_mapping/list',
  payment_mapping_delete:
    DOC2API_URL + '/invoice_calculation/payment_mapping/delete',

  // Operator Endpoints
  op_send_chat: OPERATOR_URL + '/api/v1/run-task',
  op_ws_chat: OPERATOR_URL.replace('https', 'wss') + '/api/v1/run-task',
  save_prompt: OPERATOR_URL + '/prompts/save_prompt',
  get_all_prompts: OPERATOR_URL + '/prompts/get_all_prompts',
  delete_prompt: OPERATOR_URL + '/prompts/delete_prompt',
  download_operator_files: OPERATOR_URL + '/download_files',

  // Docnet Endpoints — HTTP on docnet, WebSocket on docnet-sticky.
  docnet_chat: DOCNET_URL + '/api/v2/orchestrator/chat',
  docnet_mission_resume: DOCNET_URL + '/api/v2/orchestrator/mission',
  get_docnet_tools: DOCNET_URL + '/available_tools',
  docnet_ws_chat: DOCNET_STICKY_URL.replace('https', 'wss') + '/ws',
  docnet_ws: DOCNET_STICKY_URL + '/ws/v2',

  // Agent Setup Endpoints (via Docnet) — v2 with Bearer token auth
  get_agents: DOCNET_URL + '/api/v2/agents',
  get_agent: DOCNET_URL + '/api/v2/agents/',
  create_agent: DOCNET_URL + '/api/v2/agents',
  update_agent: DOCNET_URL + '/api/v2/agents/',
  delete_agent: DOCNET_URL + '/api/v2/agents/',
  toggle_agent: DOCNET_URL + '/api/v2/agents/',
  agent_custom_instructions: DOCNET_URL + '/api/v2/agents/',
  // Skills list — only exposed on v2 (same Bearer token as the other
  // agent endpoints). Was previously pointing at /v1, which 404s and
  // silently emptied the skill/tools UI.
  get_agent_skills: DOCNET_URL + '/api/v2/agents/skills',
  // Tool metadata (currently: which tools are flagged ``critical`` —
  // i.e., always require human approval under default approval mode).
  // Backend is canonical; frontend caches once per session to avoid
  // mirroring a security-sensitive list and risking drift.
  get_agent_tool_metadata: DOCNET_URL + '/api/v2/agents/tools/metadata',
  // MCP tool discovery
  mcp_discover: DOCNET_URL + '/api/v2/tools/mcp/discover',

  // DocNet Orchestration Endpoints — v2 with Bearer token auth
  docnet_issues: DOCNET_URL + '/api/v2/issues',
  docnet_issue_comments: DOCNET_URL + '/api/v2/issues/comments',
  docnet_labels: DOCNET_URL + '/api/v2/labels',
  docnet_approvals: DOCNET_URL + '/api/v2/approvals',
  docnet_attachments: DOCNET_URL + '/api/v2/attachments',
  docnet_missions: DOCNET_URL + '/api/v2/missions',
  docnet_projects: DOCNET_URL + '/api/v2/projects',
  docnet_inbox: DOCNET_URL + '/api/v2/inbox',
  docnet_dashboard_stats: DOCNET_URL + '/api/v2/dashboard/stats',
  docnet_emails: DOCNET_URL + '/api/v2/emails',
  docnet_processes: DOCNET_URL + '/api/v2/processes',
  docnet_delegation_status: DOCNET_URL + '/api/v2/agents/delegation-status',
  docnet_audit: DOCNET_URL + '/api/v2/audit',
  docnet_agent_runs: DOCNET_URL + '/api/v2/agents/', // + {id}/runs
  // Agent Memory & Learning system (Stage 1 MVP — keyword recall,
  // user feedback as highest-signal source, async self-reflection).
  docnet_agent_memories: DOCNET_URL + '/api/v2/agent-memories',
  docnet_agent_memory_stats: DOCNET_URL + '/api/v2/agent-memories/stats',
  docnet_agent_run_feedback: DOCNET_URL + '/api/v2/agent-runs/', // + {run_id}/feedback
  docnet_dashboard_trends: DOCNET_URL + '/api/v2/dashboard/trends',
  docnet_dashboard_widgets: DOCNET_URL + '/api/v2/dashboard/widgets',
  docnet_integrations: DOCNET_URL + '/api/v2/integrations',

  // Discord Integration Endpoints (via doc2-api)
  discord_list: DOC2API_URL + '/discord',
  discord_create: DOC2API_URL + '/discord',
  discord_get: DOC2API_URL + '/discord/', // + {id}
  discord_update: DOC2API_URL + '/discord/', // + {id}
  discord_delete: DOC2API_URL + '/discord/', // + {id}
  discord_test: DOC2API_URL + '/discord/', // + {id}/test

  // Slack Integration Endpoints (via doc2-api)
  slack_list: DOC2API_URL + '/slack',
  slack_create: DOC2API_URL + '/slack',
  slack_get: DOC2API_URL + '/slack/', // + {id}
  slack_update: DOC2API_URL + '/slack/', // + {id}
  slack_delete: DOC2API_URL + '/slack/', // + {id}
  slack_test: DOC2API_URL + '/slack/', // + {id}/test

  // Microsoft Teams Integration Endpoints (via doc2-api)
  teams_list: DOC2API_URL + '/teams',
  teams_create: DOC2API_URL + '/teams',
  teams_get: DOC2API_URL + '/teams/', // + {id}
  teams_update: DOC2API_URL + '/teams/', // + {id}
  teams_delete: DOC2API_URL + '/teams/', // + {id}
  teams_test: DOC2API_URL + '/teams/', // + {id}/test

  // XSLT Endpoints
  get_xslt: DOC2API_URL + '/xml_export/xslt/list',

  //TDIDF Endpoints
  get_tfidf_preferences:
    DOC2API_URL + '/preferences/get_tfidf_preferences_by_org_id',
  delete_tfidf_preference:
    DOC2API_URL + '/preferences/remove_tfidf_preferences',

  //PerformanceLogs Endpoints
  get_process_frequency: DOC2API_URL + '/log_service/process_frequency',
  get_service_metrics_summary:
    DOC2API_URL + '/log_service/service_metrics_summary',
  get_avg_idle_time_by_service:
    DOC2API_URL + '/log_service/avg_idle_time_by_service',
  get_status_metrics_summary:
    DOC2API_URL + '/log_service/status_metrics_summary',

  //System Logs Endpoints
  get_system_logs: DOC2API_URL + '/v2/system/logs',
  get_system_logs_count: DOC2API_URL + '/v2/system/logs_count',
  get_system_logs_summary: DOC2API_URL + '/v2/system/logs/error-summary',
  /**
   * Organization-wide activity logs (admin only)
   */
  get_organization_logs: DOC2API_URL + '/api/logs/organization',
  get_organization_logs_health: DOC2API_URL + '/api/logs/organization/health',
  get_organization_logs_metrics: DOC2API_URL + '/api/logs/organization/metrics',
  get_organization_logs_auth_success_rate:
    DOC2API_URL + '/api/logs/organization/metrics/auth-success-rate',
  get_organization_logs_auth_latency:
    DOC2API_URL + '/api/logs/organization/metrics/auth-latency',
  get_organization_logs_api_usage:
    DOC2API_URL + '/api/logs/organization/metrics/api-usage',
  get_organization_logs_analytics_timeline:
    DOC2API_URL + '/api/logs/organization/analytics',
  get_organization_logs_analytics_faro:
    DOC2API_URL + '/api/logs/organization/analytics/faro',
  get_organization_logs_analytics_combined:
    DOC2API_URL + '/api/logs/organization/analytics/combined',
  get_organization_logs_analytics_service_timeline:
    DOC2API_URL + '/api/logs/organization/analytics/service-timeline',
  // Real endpoint that queries OpenSearch for per-org distinct services.
  // The older /api/logs/services path does not exist on the backend — it
  // 404s, which is why ActivityLogs silently fell back to a hard-coded
  // list and the service filter never matched real log entries.
  get_available_services: DOC2API_URL + '/api/logs/organization/services',
  /**
   * Log Export API endpoints (async export with S3 storage)
   */
  logs_export_request: DOC2API_URL + '/api/logs/export/request',
  logs_export_status: DOC2API_URL + '/api/logs/export/status',
  logs_export_download: DOC2API_URL + '/api/logs/export/download',
  get_system_health: DOC2API_URL + '/v2/system/health',
  get_system_component_health:
    DOC2API_URL + '/v2/system/health/component/:component_name',
  get_system_info: DOC2API_URL + '/v2/system/info',
  get_system_metrics: DOC2API_URL + '/v2/system/metrics',

  /**
   * Test Results API endpoints
   */
  get_test_results_summary: DOC2API_URL + '/api/test-results/summary',
  get_test_results_by_service: DOC2API_URL + '/api/test-results/by-service',
  get_test_results_allure: DOC2API_URL + '/api/analytics/test-results/allure',

  check_duplicate_config: IMPORT_EMAIL_URL + '/email_import/check_duplicate_import',

  watchdog_releases: DOC2API_URL + '/resource/watchdog-releases',

  /**
   * Ideas / Idea Board (cross-org)
   */
  ideas_list: IDEAS_URL + '/api/ideas',
  ideas_my_votes: IDEAS_URL + '/api/ideas/my-votes',
  ideas_upload: IDEAS_URL + '/api/ideas/upload',

  /**
   * Vertex Tax Integration
   */
  vertex_credentials: DOC2API_URL + '/vertex/credentials',
  vertex_credentials_test: DOC2API_URL + '/vertex/credentials/test_credentials',

  /**
   * Script AI Chat
   */
  scriptAiChat: DOC2API_URL + '/api/v1/organisation/script/ai-chat',
};

// Every URL in apiInternal was built from the region-baked constants above
// (DOC2API_URL, OPERATOR_URL, …), so no per-access rewriting is needed.
// A region switch reloads the page (see setApiRegion), which rebuilds them.
export const api = apiInternal;

export async function getAuthorizationToken() {
  const token = await authApiService.getToken();
  return token;
}

export function getClientAuthorization() {
  // Fixed client credentials with corrected secret (788 not 7x8)
  const clientId = process.env.APP_VUE_CLIENT_ID || 'DocBits';
  const clientSecret =
    process.env.APP_VUE_CLIENT_SECRET ||
    'dfyykB8aHbACjJ9VV788tiOkWMAiqTtzSYIvvUFdNssPujYD';

  // Ensure we always use the correct client secret if the environment has the old one
  const correctedSecret = clientSecret.includes('7x8')
    ? 'dfyykB8aHbACjJ9VV788tiOkWMAiqTtzSYIvvUFdNssPujYD'
    : clientSecret;

  return 'Basic ' + btoa(clientId + ':' + correctedSecret);
}

export function getVendorsListAPI(text: string) {
  return (
    DOC2API_URL +
    '/fuzzy/get_vendors_list?keyword=' +
    text +
    '&address_type=Discrete&skip=0&limit=15'
  );
}

export function getMediVendorsListAPI(text: string) {
  return (
    DOC2API_URL + '/medi/get_customer_list?keyword=' + text + '&skip=0&limit=30'
  );
}

export function getRegexSaveUrl(type: string) {
  let url = api.update_document_type_regexes;
  if (type === 'filter_regexes') {
    url = api.update_fellow_kv_regexes;
  }

  return url;
}
